import React, { useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

class StreamingWavPlayer {
  private audioContext: AudioContext;
  private sampleRate: number = 0;
  private numChannels: number = 0;
  private headerParsed: boolean = false;
  private headerBuffer: Uint8Array = new Uint8Array(44);
  private headerBytesReceived: number = 0;
  private nextStartTime: number = 0;
  private isPlaying: boolean = false;
  private minBufferSize: number = 16384;
  private pcmData: Uint8Array = new Uint8Array(0);

  constructor() {
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }

  private parseWavHeader(header: Uint8Array): void {
    const view = new DataView(header.buffer);

    const riff = String.fromCharCode.apply(null, Array.from(header.slice(0, 4)));
    const wave = String.fromCharCode.apply(null, Array.from(header.slice(8, 12)));

    if (riff !== 'RIFF' || wave !== 'WAVE') {
      throw new Error('Invalid WAV file');
    }

    this.numChannels = view.getUint16(22, true);
    this.sampleRate = view.getUint32(24, true);
    const bitsPerSample = view.getUint16(34, true);

    console.log(`WAV Format: ${this.sampleRate}Hz, ${this.numChannels} channels, ${bitsPerSample} bits`);

    this.headerParsed = true;
  }

  private appendPcmData(newData: Uint8Array): void {
    const newBuffer = new Uint8Array(this.pcmData.length + newData.length);
    newBuffer.set(this.pcmData);
    newBuffer.set(newData, this.pcmData.length);
    this.pcmData = newBuffer;
  }

  private async tryPlayBuffer(): Promise<void> {
    if (!this.headerParsed || this.pcmData.length < this.minBufferSize) {
      return;
    }

    const bytesPerSample = this.numChannels * 2;
    const samplesToPlay = Math.floor(this.pcmData.length / bytesPerSample);
    const bytesToPlay = samplesToPlay * bytesPerSample;

    if (bytesToPlay === 0) return;

    const dataToPlay = this.pcmData.slice(0, bytesToPlay);
    this.pcmData = this.pcmData.slice(bytesToPlay);

    const audioBuffer = this.audioContext.createBuffer(
      this.numChannels,
      samplesToPlay,
      this.sampleRate
    );

    const int16Data = new Int16Array(dataToPlay.buffer, dataToPlay.byteOffset, samplesToPlay * this.numChannels);

    for (let channel = 0; channel < this.numChannels; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      for (let i = 0; i < samplesToPlay; i++) {
        channelData[i] = int16Data[i * this.numChannels + channel] / 32768;
      }
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.audioContext.destination);

    const currentTime = this.audioContext.currentTime;
    const startTime = Math.max(currentTime, this.nextStartTime);

    source.start(startTime);

    this.nextStartTime = startTime + audioBuffer.duration;
    this.isPlaying = true;

    if (this.pcmData.length >= this.minBufferSize) {
      setTimeout(() => this.tryPlayBuffer(), 10);
    }
  }

  public addChunk(chunk: Uint8Array): void {
    if (!this.headerParsed) {
      const headerBytesNeeded = 44 - this.headerBytesReceived;
      const bytesToCopy = Math.min(headerBytesNeeded, chunk.length);

      this.headerBuffer.set(
        chunk.slice(0, bytesToCopy),
        this.headerBytesReceived
      );

      this.headerBytesReceived += bytesToCopy;

      if (this.headerBytesReceived >= 44) {
        this.parseWavHeader(this.headerBuffer);

        if (chunk.length > bytesToCopy) {
          this.appendPcmData(chunk.slice(bytesToCopy));
        }
      }
    } else {
      this.appendPcmData(chunk);
    }

    this.tryPlayBuffer();
  }

  public stop(): void {
    this.audioContext.close();
    this.isPlaying = false;
  }
}

interface TTSHistoryItem {
  id: string;
  text: string;
  seed: number;
  temperature: number;
  cfgAlpha: number;
  audioBlob: Blob; // Store Blob directly
  starred: boolean;
  timestamp?: number; // Optional for backward compatibility
}

function getApiKeyFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('api-key');
}

const DEFAULT_TEMPERATURE = 0.75;
const DEFAULT_CFG_ALPHA = 2.25;

const TRUNCATE_LENGTH = 200;

// IndexedDB helpers
const DB_NAME = 'tts-db';
const STORE_NAME = 'history';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function getAllHistory(): Promise<TTSHistoryItem[]> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result as TTSHistoryItem[]);
        req.onerror = () => reject(req.error);
      })
  );
}

function putHistoryItem(item: TTSHistoryItem): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.put(item);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      })
  );
}

function deleteHistoryItem(id: string): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.delete(id);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      })
  );
}

function clearHistoryStore(): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      })
  );
}

const App = () => {
  const [text, setText] = useState('');
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1e9));
  const [temperature, setTemperature] = useState(DEFAULT_TEMPERATURE);
  const [cfgAlpha, setCfgAlpha] = useState(DEFAULT_CFG_ALPHA);
  const [history, setHistory] = useState<TTSHistoryItem[]>([]);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [pauseDuration, setPauseDuration] = useState(1.0);
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const streamingPlayerRef = useRef<StreamingWavPlayer | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // On mount, load from IndexedDB
  useEffect(() => {
    getAllHistory().then((items) => {
      // Sort by timestamp (newest first), with fallback for items without timestamp
      const sortedItems = items.sort((a, b) => {
        const timeA = a.timestamp || 0;
        const timeB = b.timestamp || 0;
        return timeB - timeA;
      });
      setHistory(sortedItems);
    });
  }, []);

  // On history change, update audioUrls
  useEffect(() => {
    const urls: Record<string, string> = {};
    history.forEach((item) => {
      urls[item.id] = URL.createObjectURL(item.audioBlob);
    });
    setAudioUrls(urls);
    return () => {
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [history]);

  useEffect(() => {
    const key = getApiKeyFromUrl();
    if (!key) {
      setError('Missing API key. URL should look like: api-inv.kyutai.org/?api-key=YOUR_API_KEY');
    } else {
      setApiKey(key);
    }
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.src = '';
      }
      if (streamingPlayerRef.current) {
        streamingPlayerRef.current.stop();
      }
    };
  }, [currentAudio]);

  const generateAudio = async () => {
    if (!apiKey || !text.trim()) return;
    setLoading(true);

    // Stop any currently playing audio and clear timer
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.src = '';
      setCurrentAudio(null);
    }
    if (updateTimerRef.current) {
      clearInterval(updateTimerRef.current);
      updateTimerRef.current = null;
    }
    if (streamingPlayerRef.current) {
      streamingPlayerRef.current.stop();
      streamingPlayerRef.current = null;
    }

    try {
      const query = {
        text: text,
        seed,
        temperature,
        top_k: 250,
        cfg_alpha: cfgAlpha,
        voice: 'chunk_00212.wav',
      };

      const response = await fetch('/api/tts_streaming', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'kyutai-api-key': apiKey,
        },
        body: JSON.stringify(query),
      });

      if (!response.ok) {
        const errorText = await response.text();
        alert(`Error: ${errorText}`);
        setLoading(false);
        return;
      }

      // Clone the response so we can use it twice
      const responseForPlayback = response.clone();
      const responseForHistory = response.clone();

      // Start streaming playback with StreamingWavPlayer
      const reader = responseForPlayback.body!.getReader();
      const player = new StreamingWavPlayer();
      streamingPlayerRef.current = player;

      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            if (value) {
              player.addChunk(value);
            }
          }
        } catch (e) {
          console.error('Error processing stream:', e);
        }
      };

      // Start processing the stream
      processStream();

      // Also collect the full blob for history
      const blob = await responseForHistory.blob();

      const newItem: TTSHistoryItem = {
        id: uuidv4(),
        text,
        seed,
        temperature,
        cfgAlpha,
        audioBlob: blob,
        starred: false,
        timestamp: Date.now(),
      };
      try {
        await putHistoryItem(newItem);
        setHistory((prev) => [newItem, ...prev]);
      } catch (e: any) {
        alert(e?.message || String(e));
      }
    } catch (e) {
      console.error('Error generating audio:', e);
      alert('Network error or failed to fetch');
    }
    setLoading(false);
  };

  const clearHistory = async () => {
    setHistory([]);
    await clearHistoryStore();
    Object.values(audioUrls).forEach((url) => URL.revokeObjectURL(url));
    setAudioUrls({});
  };

  const deleteEntry = async (id: string) => {
    setHistory((prev) => prev.filter((item) => item.id !== id));
    await deleteHistoryItem(id);
    if (audioUrls[id]) {
      URL.revokeObjectURL(audioUrls[id]);
      setAudioUrls((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  const toggleStar = async (id: string) => {
    setHistory((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, starred: !item.starred };
          putHistoryItem(updated);
          return updated;
        }
        return item;
      })
    );
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  if (error) {
    return <div className="p-4 text-red-600 text-center">{error}</div>;
  }

  return (
    <div className="max-w-xl mx-auto p-4 space-y-4 sm:px-4">
      <h1 className="text-xl font-bold text-center">TTS Voice Generator</h1>
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full border rounded p-2"
        placeholder="Enter your text here..."
        rows={4}
      />
      <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-4 space-y-2 sm:space-y-0">
        <div className="flex-1">
          <div className="text-sm mb-1">Seed: {seed}</div>
          <button
            onClick={() => setSeed(Math.floor(Math.random() * 1e9))}
            className="w-full sm:w-auto px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Re-randomize
          </button>
        </div>
        <div className="flex-1">
          <div className="text-sm mb-1">Temperature: {temperature.toFixed(2)}</div>
          <div className="flex space-x-1">
            <button
              onClick={() => setTemperature((t) => Math.max(0, +(t - 0.25).toFixed(2)))}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              -
            </button>
            <button
              onClick={() => setTemperature((t) => +(t + 0.25).toFixed(2))}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              +
            </button>
          </div>
        </div>
        <div className="flex-1">
          <div className="text-sm mb-1">CFG: {cfgAlpha.toFixed(2)}</div>
          <div className="flex space-x-1">
            <button
              onClick={() => setCfgAlpha((c) => Math.max(0, +(c - 0.25).toFixed(2)))}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              -
            </button>
            <button
              onClick={() => setCfgAlpha((c) => +(c + 0.25).toFixed(2))}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              +
            </button>
          </div>
        </div>
        <div className="flex-1">
          <div className="text-sm mb-1">Pause: {pauseDuration.toFixed(2)}s</div>
          <div className="flex space-x-1 mb-1">
            <button
              onClick={() => setPauseDuration((p) => Math.max(0.25, +(p - 0.25).toFixed(2)))}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              -
            </button>
            <button
              onClick={() => setPauseDuration((p) => +(p + 0.25).toFixed(2))}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              +
            </button>
          </div>
          <button
            onClick={() => {
              if (textareaRef.current) {
                const textarea = textareaRef.current;
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const pauseTag = `<break time="${pauseDuration}s" />`;
                const newText = text.slice(0, start) + pauseTag + text.slice(end);
                setText(newText);
                // Set cursor position after the inserted tag
                setTimeout(() => {
                  textarea.selectionStart = textarea.selectionEnd = start + pauseTag.length;
                  textarea.focus();
                }, 0);
              }
            }}
            className="w-full px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
          >
            Add pause
          </button>
        </div>
      </div>

      <button
        className={`w-full px-4 py-2 rounded text-white ${
          loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        onClick={generateAudio}
        disabled={!text.trim() || loading}
      >
        {loading ? (
          <div className="flex justify-center items-center space-x-2">
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            <span>Generating...</span>
          </div>
        ) : (
          'Generate Audio'
        )}
      </button>

      <div className="pt-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">History</h2>
          {history.length > 0 && (
            <button
              onClick={clearHistory}
              className="text-sm px-4 py-2 border rounded hover:bg-gray-100"
            >
              Clear History
            </button>
          )}
        </div>
        {history.length === 0 && <p className="text-sm text-gray-600">No audio generated yet.</p>}
        <ul className="space-y-4">
          {history.map((item) => {
            const isExpanded = expandedIds.has(item.id);
            const shouldTruncate = item.text.length > TRUNCATE_LENGTH;
            const audioUrl = audioUrls[item.id];

            return (
              <li
                key={item.id}
                className={`border rounded p-4 relative ${
                  item.starred ? 'bg-yellow-50 border-yellow-400' : ''
                }`}
              >
                {/* Trash bin top left */}
                <button
                  title="Delete entry"
                  onClick={() => deleteEntry(item.id)}
                  className="text-gray-500 hover:text-gray-700 absolute top-2 left-2"
                  aria-label="Delete entry"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5-4h4m-4 0a1 1 0 00-1 1v1h6V4a1 1 0 00-1-1m-4 0h4"
                    />
                  </svg>
                </button>

                {/* Star top right */}
                <button
                  title={item.starred ? 'Unstar' : 'Star'}
                  onClick={() => toggleStar(item.id)}
                  className="text-yellow-500 hover:text-yellow-600 absolute top-2 right-2"
                  aria-label={item.starred ? 'Unstar entry' : 'Star entry'}
                >
                  {item.starred ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 fill-current"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 stroke-current"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.92-.755 1.688-1.54 1.118l-3.371-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.035 9.384c-.783-.57-.38-1.81.588-1.81h4.163a1 1 0 00.95-.69l1.285-3.957z"
                      />
                    </svg>
                  )}
                </button>

                <div className="mt-2">
                  <div className="text-sm">
                    Seed: {item.seed}
                    {' | '}
                    Temperature: {item.temperature.toFixed(2)}
                    {' | '}
                    CFG: {(item.cfgAlpha ?? DEFAULT_CFG_ALPHA).toFixed(2)}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap">
                    {shouldTruncate && !isExpanded
                      ? item.text.slice(0, TRUNCATE_LENGTH) + '...'
                      : item.text}
                  </p>
                  {shouldTruncate && (
                    <button
                      onClick={() => toggleExpand(item.id)}
                      className="mt-1 text-blue-600 text-sm hover:underline"
                    >
                      {isExpanded ? 'Collapse' : 'Expand'}
                    </button>
                  )}
                  <audio controls src={audioUrl} className="w-full mt-2" />
                  <a
                    href={audioUrl}
                    download={`tts-${item.seed}.wav`}
                    className="text-blue-600 text-sm mt-1 inline-block"
                  >
                    Download
                  </a>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default App;